import type { EdgelessLineGridPalette } from '@blocksuite/affine-shared/styles';
import { CANVAS_WORLD_MAX, CANVAS_WORLD_MIN } from '@blocksuite/store';

import { planEdgelessLineGrid } from './line-grid-plan.js';

interface LineGridViewport {
  width: number;
  height: number;
  left: number;
  top: number;
  gap: number;
  translateX: number;
  translateY: number;
  pixelRatio: number;
}

type LayeredLineGridViewport = Omit<LineGridViewport, 'gap'> & { zoom: number };

function lineStarts(
  extent: number,
  translation: number,
  origin: number,
  gap: number,
  pixelRatio: number,
  lineWidth: number
) {
  const step = gap * pixelRatio;
  // Reduce large pans, but never round the gap: each world line is projected
  // independently, so pixel snapping cannot accumulate drift across the view.
  const phase = (translation % gap) * pixelRatio + origin;
  const first = Math.floor(-phase / step);
  const count = Math.ceil(extent / step) + 2;
  return Array.from({ length: count }, (_, index) =>
    Math.round(phase + (first + index) * step - lineWidth / 2)
  ).filter(position => position < extent && position + lineWidth > 0);
}

/** All geometry is in physical pixels; only the final CSS placement is scaled. */
export function calculateLineGridGeometry(
  viewport: LineGridViewport,
  lineWidthCss = 0.5
) {
  const { width, height, left, top, gap, translateX, translateY, pixelRatio } =
    viewport;
  if (
    !Object.values(viewport).every(Number.isFinite) ||
    width <= 0 ||
    height <= 0 ||
    gap <= 0 ||
    !Number.isFinite(lineWidthCss) ||
    lineWidthCss <= 0 ||
    pixelRatio <= 0
  ) {
    return null;
  }

  const originX = left * pixelRatio - Math.floor(left * pixelRatio);
  const originY = top * pixelRatio - Math.floor(top * pixelRatio);
  const pixelWidth = Math.ceil(width * pixelRatio + originX);
  const pixelHeight = Math.ceil(height * pixelRatio + originY);
  // Round coverage up, then compensate with alpha so fractional DPRs preserve
  // each tier's intended visual weight without antialiased/subpixel edges.
  const lineWidth = Math.max(1, Math.ceil(lineWidthCss * pixelRatio));

  return {
    width: pixelWidth,
    height: pixelHeight,
    lineWidth,
    // Keep the thin grid's visual weight without relying on subpixel strokes.
    opacity: Math.min(1, (lineWidthCss * pixelRatio) / lineWidth),
    x: lineStarts(pixelWidth, translateX, originX, gap, pixelRatio, lineWidth),
    y: lineStarts(pixelHeight, translateY, originY, gap, pixelRatio, lineWidth),
    position: `${-originX / pixelRatio}px ${-originY / pixelRatio}px`,
    size: `${pixelWidth / pixelRatio}px ${pixelHeight / pixelRatio}px`,
  };
}

/** One non-repeating image with uninterrupted lines, not small SVG tiles. */
export function createEdgelessLineGridBackground(
  viewport: LayeredLineGridViewport,
  palette: EdgelessLineGridPalette
) {
  if (!Number.isFinite(viewport.zoom) || viewport.zoom <= 0) return null;
  const layers = [];
  for (const layer of planEdgelessLineGrid(viewport.zoom)) {
    // Hidden detail layers must not generate dense geometry at overview zoom.
    if (layer.opacity <= 0) continue;
    const geometry = calculateLineGridGeometry(
      { ...viewport, gap: layer.screenGap },
      layer.lineWidth
    );
    if (!geometry) return null;
    layers.push({ ...layer, geometry });
  }
  const geometry = layers[0]?.geometry;
  if (!geometry) return null;
  const { width, height } = geometry;
  const paths = layers.map(({ level, opacity, geometry }) => {
    const { lineWidth, x, y } = geometry;
    const path = [
      ...x.map(left => `M${left} 0h${lineWidth}v${height}h-${lineWidth}Z`),
      ...y.map(top => `M0 ${top}h${width}v${lineWidth}h-${width}Z`),
    ];
    const fill = (palette[level].trim() || 'rgba(127, 127, 127, 0.35)')
      .replaceAll('&', '&amp;')
      .replaceAll('"', '&quot;')
      .replaceAll('<', '&lt;');
    // One filled path per layer avoids extra alpha at its own intersections.
    // Layers intentionally overlap, preserving CodeCanvas's visual hierarchy.
    return `<path d="${path.join('')}" fill="${fill}" opacity="${opacity * geometry.opacity}"/>`;
  });
  const originX =
    viewport.left * viewport.pixelRatio -
    Math.floor(viewport.left * viewport.pixelRatio);
  const originY =
    viewport.top * viewport.pixelRatio -
    Math.floor(viewport.top * viewport.pixelRatio);
  const worldX = (value: number) =>
    Math.round(
      (viewport.translateX + value * viewport.zoom) * viewport.pixelRatio +
        originX
    );
  const worldY = (value: number) =>
    Math.round(
      (viewport.translateY + value * viewport.zoom) * viewport.pixelRatio +
        originY
    );
  const clipX = worldX(CANVAS_WORLD_MIN),
    clipY = worldY(CANVAS_WORLD_MIN);
  const clipWidth = worldX(CANVAS_WORLD_MAX) - clipX,
    clipHeight = worldY(CANVAS_WORLD_MAX) - clipY;
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}"><defs><clipPath id="world"><rect x="${clipX}" y="${clipY}" width="${clipWidth}" height="${clipHeight}"/></clipPath></defs><g clip-path="url(#world)">${paths.join('')}</g></svg>`;
  return {
    image: `url("data:image/svg+xml,${encodeURIComponent(svg)}")`,
    position: geometry.position,
    size: geometry.size,
  };
}
